import { useState, useCallback, useEffect } from 'react';

// Helper to get query params
const getQueryParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
        userId: params.get('userId'),
        password: params.get('password'),
        uniqueIdentifier: params.get('uniqueIdentifier')
    };
};

const getUserToken = async (loginToken: string) => {
    const { uniqueIdentifier } = getQueryParams();
    if (!uniqueIdentifier) {
        throw new Error('uniqueIdentifier is required');
    }

    const response = await fetch('https://dhanaprayoga.fiu.finfactor.in/pfm/api/v2/user-token', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${loginToken}`
        },
        body: JSON.stringify({ uniqueIdentifier })
    });
    const data = await response.json();
    return data.token;
};

// Login API call
const callLoginApi = async (userId: string, password: string) => {
    const response = await fetch('https://dhanaprayoga.fiu.finfactor.in/pfm/api/v2/user-login', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify({ userId, password })
    });
    const data = await response.json();
    const loginToken = data.token;
    return await getUserToken(loginToken);
};

// Super simple in-memory token for demo
let globalHostToken: string | null = null;

let listeners: Array<() => void> = [];

const notifyListeners = () => listeners.forEach(listener => listener());

export const authService = {
    getToken: async (): Promise<string | null> => {
        if (!globalHostToken) {
            const { userId, password } = getQueryParams();
            if (userId && password) {
                globalHostToken = await callLoginApi(userId, password);
            }
        }
        console.log('[Host Auth] getToken called, returning:', globalHostToken);
        // Simulate async
        await new Promise(resolve => setTimeout(resolve, 50));
        return globalHostToken;
    },
    refreshToken: async (): Promise<string | null> => {
        console.log('[Host Auth] Attempting to refresh token...');
        const { userId, password } = getQueryParams();
        if (userId && password) {
            globalHostToken = await callLoginApi(userId, password);
            notifyListeners();
        }
        return globalHostToken;
    },
    subscribe: (listener: () => void) => {
        listeners.push(listener);
        return () => {
            listeners = listeners.filter(l => l !== listener);
        };
    }
};

export function useHostAuth() {
    const [currentToken, setCurrentToken] = useState<string | null>(globalHostToken);
    const [uniqueIdentifier, setUniqueIdentifier] = useState<string | null>(null);

    useEffect(() => {
        const unsubscribe = authService.subscribe(() => {
            setCurrentToken(globalHostToken);
        });

        const init = async () => {
            // Initial fetch just in case
            authService.getToken().then(setCurrentToken);

            // Set uniqueIdentifier from query params
            const { uniqueIdentifier: queryUniqueId } = await getQueryParams();
            console.log('[Host Auth] Initial uniqueIdentifier:', queryUniqueId);
            setUniqueIdentifier(queryUniqueId);
        };

        init();
        return unsubscribe;
    }, []);

    const getAuthTokenForMfe = useCallback(async () => {
        return authService.getToken();
    }, []);

    return {
        currentToken,
        getAuthTokenForMfe,
        refreshToken: authService.refreshToken,
        uniqueIdentifier,
    };
}
